/**
 * 登录态与权限。
 *
 * `permissions` 来自服务端，前端**只用它决定显示什么**。
 * 每个接口在服务端仍然独立鉴权——菜单藏起来不等于调不到。
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  authApi,
  getToken,
  setToken,
  setUnauthorizedHandler,
  type AdminIdentity,
} from "./api";

interface AuthState {
  admin: AdminIdentity | null;
  loading: boolean;
  login: (phone: string, code: string) => Promise<void>;
  logout: () => void;
  can: (permission: string) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<AdminIdentity | null>(null);
  const [loading, setLoading] = useState(true);

  // 刷新页面后用已存的 token 恢复身份；token 失效则静默退出。
  useEffect(() => {
    setUnauthorizedHandler(() => setAdmin(null));
    if (!getToken()) {
      setLoading(false);
      return;
    }
    authApi
      .me()
      .then(setAdmin)
      .catch(() => setToken(null))
      .finally(() => setLoading(false));
    return () => setUnauthorizedHandler(null);
  }, []);

  const login = useCallback(async (phone: string, code: string) => {
    const result = await authApi.login(phone, code);
    setToken(result.token);
    setAdmin(result.admin);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setAdmin(null);
  }, []);

  const permissions = useMemo(() => new Set(admin?.permissions ?? []), [admin]);
  const can = useCallback((p: string) => permissions.has(p), [permissions]);

  const value = useMemo<AuthState>(
    () => ({ admin, loading, login, logout, can }),
    [admin, loading, login, logout, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth 必须在 AuthProvider 内使用");
  return ctx;
}
